import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: "Qu'est-ce que la VAE ?",
    answer: "La VAE (Validation des Acquis de l'Expérience) est un dispositif qui permet d'obtenir un diplôme grâce à votre expérience professionnelle, sans reprendre d'études. Il faut justifier d'au moins 1 an d'expérience en rapport avec le diplôme visé."
  },
  {
    question: "Quels diplômes puis-je obtenir par la VAE ?",
    answer: "Sur notre plateforme, nous accompagnons les candidats pour le CAP AEPE, le DEAP, le DEAS, le DEEJE, le CAFERUIS, le BAC ASSP et le Titre Professionnel IEPE. Ces diplômes couvrent le secteur sanitaire, social et de la petite enfance."
  },
  {
    question: "Quelle est la différence entre les formules Bronze, Silver et Gold ?",
    answer: "La formule Bronze vous donne accès à la plateforme e-learning complète (cours, quiz, supports) et au Livret 2 détaillé pour travailler en autonomie. La formule Silver ajoute un accompagnement personnalisé : 5 séances d'accompagnement (1 par correction), aide à la rédaction du Livret 2, 1 oral blanc et 1 séance sur la présentation personnelle. La formule Gold offre un accompagnement premium illimité pendant 1 an avec des corrections illimitées, des séances d'explication à chaque correction, 3 oraux blancs avec debriefing et la préparation à la présentation personnelle."
  },
  {
    question: "Combien de temps dure l'accompagnement ?",
    answer: "L'accès à la plateforme et à votre formule est valable 1 an à compter de la date d'achat. La durée de votre parcours VAE dépend de votre rythme : en moyenne entre 6 et 12 mois."
  },
  {
    question: "Comment se déroulent les séances de coaching ?",
    answer: "Les séances de coaching se font en ligne (visioconférence). Vous réservez vos créneaux directement sur la plateforme en fonction des disponibilités. Chaque séance est individuelle et adaptée à votre avancement."
  },
  {
    question: "Qu'est-ce que l'Urgence VAE ?",
    answer: "L'Urgence VAE est une formule intensive pour les candidats dont la date de dépôt du Livret 2 ou la date de jury approche. Deux options : Urgence Dépôt Livret 2 (490 €) pour finaliser votre dossier, ou Urgence Oral (590 €) pour préparer votre passage devant le jury."
  },
  {
    question: "Est-ce que la VAE est finançable par le CPF ?",
    answer: "Oui, la VAE est éligible au CPF (Compte Personnel de Formation). Cette option sera bientôt disponible sur notre plateforme. En attendant, les paiements s'effectuent directement en ligne. N'hésitez pas à nous contacter pour en savoir plus."
  },
  {
    question: "Comment fonctionne la correction du Livret 2 ?",
    answer: "Avec la formule Silver, vous bénéficiez de 5 corrections approfondies de votre Livret 2 (1 par bloc). Vous envoyez votre travail, Rose le relit et l'annote en détail, puis vous recevez un appel explicatif pour chaque correction afin de bien comprendre les améliorations à apporter."
  },
  {
    question: "Comment se passe l'oral blanc ?",
    answer: "L'oral blanc simule les conditions réelles du passage devant le jury. Il dure environ 1 heure et comprend une mise en situation suivie d'un débrief détaillé avec des conseils personnalisés pour améliorer votre présentation."
  },
  {
    question: "Je n'ai pas de diplôme, puis-je quand même faire une VAE ?",
    answer: "Oui, absolument ! La VAE est justement faite pour valoriser votre expérience professionnelle. Peu importe votre niveau de diplôme actuel, si vous avez au moins 1 an d'expérience dans le domaine visé, vous pouvez entreprendre une VAE."
  }
];

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)' }}>
      <section style={{ padding: '4rem 1.2rem 3rem', background: 'var(--gradient-hero)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <Link to="/" className="body-small" style={{ textDecoration: 'none', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
            <ArrowLeft size={16} /> Retour à l'accueil
          </Link>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>Questions Fréquentes</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Tout ce que vous devez savoir sur la VAE et notre accompagnement</p>
        </div>
      </section>

      <section style={{ padding: '3rem 1.2rem' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {FAQ_ITEMS.map((item, index) => (
              <div key={index} className="card" style={{
                padding: 0, overflow: 'hidden', cursor: 'pointer',
                border: openIndex === index ? '2px solid #E75480' : '1px solid var(--border-light)',
                transition: 'border 0.2s'
              }}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                data-testid={`faq-item-${index}`}
              >
                <div style={{
                  padding: '1.25rem 1.5rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: openIndex === index ? '#FFF0F3' : 'transparent'
                }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', flex: 1, marginRight: '1rem' }}>
                    {item.question}
                  </h3>
                  {openIndex === index ? <ChevronUp size={20} color="#E75480" /> : <ChevronDown size={20} />}
                </div>
                {openIndex === index && (
                  <div style={{ padding: '0 1.5rem 1.25rem', lineHeight: 1.7 }}>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <p className="body-large" style={{ marginBottom: '1rem', color: '#4A3050' }}>
              Vous avez d'autres questions ?
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="mailto:apdr.vae@gmail.com" className="btn-primary" style={{ textDecoration: 'none' }}>
                Nous écrire
              </a>
              <a href="https://wa.me/33688399624" target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ textDecoration: 'none' }}>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;
